import React from 'react';
import { useDmaBoundaries } from './shared/useDmaBoundaries';
import NationalChoropleth, { type ChoroplethLabel } from './NationalChoropleth';

// CTV-445: national DMA choropleth — the DMA analogue of StateChoropleth. Loads
// WGS84 DMA boundaries (census_dmas, via the dma-boundaries endpoint), keys
// features by 3-digit dma_code — the same codes our geo endpoint returns — and
// renders through the shared NationalChoropleth, so the DMA and State maps use
// the exact same projection/framing. We label only the *delivered* markets
// inside their borders (labeling all 210 would be unreadable); empty markets
// rely on the hover tooltip.

interface DmaDatum {
  dma: string; // 3-digit Nielsen code
  impressions: number;
  completes: number;
}

const dmaKeyOf = (f: GeoJSON.Feature): string | null => {
  const code = (f.properties as Record<string, unknown>)?.dma_code;
  return code ? String(code) : null;
};
const dmaNameOf = (f: GeoJSON.Feature): string => String((f.properties as Record<string, unknown>)?.dma_name ?? '');

// Area-weighted centroid + |area| of a polygon's outer ring (shoelace formula).
// The centroid follows the shape's actual mass, so it reads as centered for
// irregular/coastal markets where a bbox center would sit off to one side.
function ringCentroid(ring: number[][]): { area: number; cx: number; cy: number } {
  let a = 0;
  let cx = 0;
  let cy = 0;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const [x0, y0] = ring[i] as [number, number];
    const [x1, y1] = ring[(i + 1) % n] as [number, number];
    const cross = x0 * y1 - x1 * y0;
    a += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  a *= 0.5;
  if (Math.abs(a) < 1e-12) {
    // Degenerate ring — average the vertices.
    let sx = 0;
    let sy = 0;
    for (const pt of ring) {
      sx += (pt as [number, number])[0];
      sy += (pt as [number, number])[1];
    }
    return { area: 0, cx: sx / Math.max(1, n), cy: sy / Math.max(1, n) };
  }
  return { area: Math.abs(a), cx: cx / (6 * a), cy: cy / (6 * a) };
}

// A point to anchor the market's name inside its border: the area-weighted
// centroid of its largest polygon (so islands in a MultiPolygon don't drag the
// label off the mainland).
function featureLabelPoint(geom: GeoJSON.Geometry): [number, number] | null {
  const outerRings: number[][][] =
    geom.type === 'Polygon'
      ? [geom.coordinates[0] as number[][]]
      : geom.type === 'MultiPolygon'
        ? (geom.coordinates as number[][][][]).map((poly) => poly[0] as number[][])
        : [];
  let best: { area: number; center: [number, number] } | null = null;
  for (const ring of outerRings) {
    if (!ring || ring.length < 3) continue;
    const c = ringCentroid(ring);
    if (!best || c.area > best.area) best = { area: c.area, center: [c.cx, c.cy] };
  }
  return best ? best.center : null;
}

const DmaChoropleth: React.FC<{
  byDma: DmaDatum[];
  accentColor?: string;
  highlightKey?: string | null;
  highlightColor?: string;
}> = ({ byDma, accentColor, highlightKey, highlightColor }) => {
  const { data: geojson } = useDmaBoundaries();

  const valueByKey = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const d of byDma) m.set(d.dma, d.impressions);
    return m;
  }, [byDma]);

  // Frame the initial view on the delivered markets rather than the whole US.
  const focusKeys = React.useMemo(() => byDma.map((d) => d.dma), [byDma]);

  // Name labels for the delivered markets, anchored inside each border.
  const labels = React.useMemo<ChoroplethLabel[]>(() => {
    if (!geojson) return [];
    const wanted = new Set(byDma.map((d) => d.dma));
    const out: ChoroplethLabel[] = [];
    for (const f of geojson.features) {
      const code = dmaKeyOf(f);
      if (!code || !wanted.has(code) || !f.geometry) continue;
      const pos = featureLabelPoint(f.geometry);
      if (pos) out.push({ position: pos, text: dmaNameOf(f), key: code });
    }
    return out;
  }, [geojson, byDma]);

  return (
    <NationalChoropleth
      geojson={geojson}
      valueByKey={valueByKey}
      keyOf={dmaKeyOf}
      nameOf={dmaNameOf}
      labels={labels}
      labelBackground
      idPrefix="dma"
      focusKeys={focusKeys}
      accentColor={accentColor}
      highlightKey={highlightKey}
      highlightColor={highlightColor}
    />
  );
};

export default DmaChoropleth;
